import { AgGridReact } from 'ag-grid-react';
import { LicenseManager } from 'ag-grid-enterprise';
import { AG_GRID_LICENSE } from '../../constants';
import { useEffect, useState, useRef } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import * as StyledComponents from './StyledComponents';
import {
  useIamUserProfileStore} 
  from '../../Store/index';
import {fetchUserActivities,fetchExternalAudit} from '../../util/api';
import useAuthToken from '../../hooks/useAuthToken';
import { INPUT_SIZES, SearchInput, IconButton, IconFileDownload, BUTTON_SIZES } from 'cdk-radial';
import {IUserActivitySearch} from './interface';
import {defaultColDef,getColumnDefs} from './UserActivityGridConfig';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import CenteredSpinner from '../common/CenteredSpinner';

LicenseManager.setLicenseKey(AG_GRID_LICENSE);

export default function UserActivityContainer({
  startTime,
  endTime,
  loginId,
  ip,
  storeId,
  enterpriseId
}:IUserActivitySearch) {
  const {t} = useTranslation();
  const gridRef = useRef<AgGridReact>(null);
  const [userActivities,setUserActivities] = useState<any>();
  const iamUserInfo = useIamUserProfileStore((state:any) => state.iamUserInfo);
  let columns = getColumnDefs(iamUserInfo.fullUser?.stores, t);
  const token = useAuthToken();
  const [searchTerm, setSearchTerm] = useState<string>();
  const [filterValue, setFilterValue] =  useState<any>();
  const [isLoading, setIsLoading] =  useState(false);
  
  const storeMap = iamUserInfo.fullUser?.stores?.reduce((acc:any, store:any) => { acc[store.id] = store.name; return acc }, {} as any)

  const handleChange = (searchValue:string) => {
    setSearchTerm(searchValue);
  };

  function convertToDate(date: any) {
    return date ? moment.utc(
        date,
    'dddd, MMMM DD, YYYY at h:mm:ss A Z') :'';
  };

  useEffect(() => {
      const activity = userActivities?.filter((activity:any) =>{
        return (
          activity?.storeName?.toLowerCase().includes(searchTerm?.toLowerCase()) || 
          activity?.ip?.includes(searchTerm?.toLowerCase()) || 
          activity?.event?.toLowerCase().includes(searchTerm?.toLowerCase()))
      });
      setFilterValue(activity);
      // eslint-disable-next-line react-hooks/exhaustive-deps
  },[searchTerm]);

  const fetchData = async (searchCriteria: IUserActivitySearch) => {
    setIsLoading(true);
    let queryParams = {} as IUserActivitySearch;
    if(searchCriteria.startTime && searchCriteria.endTime){
      queryParams.startTime=searchCriteria.startTime;
      queryParams.endTime=searchCriteria.endTime;
    }
    if(loginId){
      queryParams.loginId = searchCriteria.loginId;
    }
    if(ip){
      queryParams.ip = searchCriteria.ip;
    }
    if(storeId){
      queryParams.storeId = searchCriteria.storeId;
    }
    if(enterpriseId){
      queryParams.enterpriseId = searchCriteria.enterpriseId;
    }
    if(!searchCriteria.enterpriseId || !searchCriteria.loginId ) {
      setFilterValue([]);
    }
    else {
      let res = await fetchUserActivities(token,queryParams);
      let externalAuditResponse = await fetchExternalAudit(token,queryParams);

      let response = res?.map(r =>({
        date : (moment.utc(convertToDate(r.eventDate)).local().format('MM/DD/YYYY')) + ' ' + 
        moment.utc(convertToDate(r.eventDate)).local().format('h:mm:ss A'),
        storeName: storeMap[r.storeId],
        event: r.event,
        ip:r.ip,
        system: `${r.os} (${r.device})`,
        browser: r.browser,
      })) || [];

      let auditResponse = externalAuditResponse?.map((r:any) => ({
        date : (moment(r.clientEventTime,"YYYY-MM-DD HH:mm:ss.SSS").local().format('MM/DD/YYYY h:mm:ss A')) ,
        storeName: storeMap[r.storeId],
        event: `${r.workflow} - ${r.eventType}`,
        ip:r.ipAddress,
        system: r.deviceType,
        browser: `${r.osName} ${r.osVersion}`,
      })) || [];

      let mergedResponse = [...response, ...auditResponse];
      mergedResponse = mergedResponse.sort((a:any, b:any) => b.date.localeCompare(a.date));

      setUserActivities(mergedResponse);
      setFilterValue(mergedResponse);
      setIsLoading(false);
    }
  }

  useEffect(()=>{
    const searchCriteria = {
      startTime,
      endTime,
      loginId,
      ip,
      storeId,
      enterpriseId
    } as IUserActivitySearch;
    setFilterValue([]);
    fetchData(searchCriteria);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[startTime,endTime,loginId,ip,storeId, enterpriseId, setUserActivities])

  return (
    <div style={{ margin:'5px'}}>
      <div style={{display:'flex',flexDirection:'column',margin:'5px'}} >
        <div style={{display:'flex', justifyContent:'space-between'}}>
          <div style={{ margin:'10px', fontSize: '20px', fontWeight: '500', display:'flex', flexDirection:"row"}}>
            {loginId}
            { isLoading  && loginId && <CenteredSpinner /> }
          </div>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
          <SearchInput 
            clearButtonLabel="clearButtonLabel" 
            style={{margin:'10px',width:"380px"}}
            id="search-input-expanded" 
            isAlwaysExpanded name="search-input-expanded"
            onChange={handleChange} 
            placeholder={t('pulse.enterlog')}
            size={INPUT_SIZES.STANDARD} 
            dataTestId='pulse-activity-search-input'
            value={searchTerm} />
            <IconButton 
                style={{ margin:'8px 0PX'}}
                onClick={() => { gridRef.current?.api?.exportDataAsExcel({fileName: 'user-activity-list'}) }}
                icon={<IconFileDownload />} 
                size={BUTTON_SIZES.LARGE} text="Button" />
          </div>
        </div>
      <div>
      <StyledComponents.StyledTableContainer>
            <StyledComponents.StyledTableWrapper hideHeader={false} className="ag-theme-alpine">
              <AgGridReact
                ref={gridRef}
                rowData={filterValue}
                columnDefs={columns}
                defaultColDef={defaultColDef}
                animateRows={true}
                rowSelection="single"
                pagination={true}
                paginationPageSize={10}
                excludeHiddenColumnsFromQuickFilter={true}
                suppressDragLeaveHidesColumns={true}
              />
            </StyledComponents.StyledTableWrapper>
          </StyledComponents.StyledTableContainer> 
        </div>   
      </div>
    </div>
  )
}
